import type { Dispatch, Middleware } from "@reduxjs/toolkit";

import initMixpanel, { TrackingEvent } from "../../libs/externals/mixpanel";
import coreSlice from "../slices/core";
import errorSlice from "../slices/error";
import modalSlice from "../slices/modal";
import type { CommonAppActions, CommonAppState } from "../types";

/**
 * Narrowed type for common middleware - only methods actually used.
 * Keeps middleware decoupled from variant-specific tracking methods.
 * Variants use full ReturnType<typeof initMixpanel> for their own needs.
 */
type CommonTrackingMethods = Pick<
  ReturnType<typeof initMixpanel>,
  "trackModalOpened" | "trackModalClosed" | "track" | "registerUserProfile"
>;

/**
 * Creates the common tracking middleware that handles shared Redux actions.
 */
export function createCommonTrackingMiddleware(
  mixpanel: CommonTrackingMethods
): Middleware<EmptyObject, CommonAppState, Dispatch<CommonAppActions>> {
  return store => next => _action => {
    const action = _action as CommonAppActions;

    // Capture modal state before action (for close tracking)
    const wasModalOpen = store.getState().modal.isOpen;

    next(action);

    // Track modal open actions
    if (action.type === modalSlice.actions.show.type) {
      const { modalConfig } = store.getState().modal;

      if (modalConfig) {
        mixpanel.trackModalOpened(modalConfig.contentType);
      }
    } else if (action.type === coreSlice.actions.initAppDone.type) {
      mixpanel.registerUserProfile();
    } else if (action.type === modalSlice.actions.close.type) {
      if (wasModalOpen) {
        mixpanel.trackModalClosed();
      }
    } else if (action.type === errorSlice.actions.set.type) {
      mixpanel.track(TrackingEvent.ErrorSet);
    }
  };
}
